import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {loadingIndicatorAttrs} from 'notebook/util/ExportInstrumentation';
import {BenchlingState} from 'redux-stores/reduceApp';
import APIUtil from 'util/APIUtil';
import memoizeLastCall from 'util/memoizeLastCall';
import {WorkflowSelector, WorkflowsState} from 'workflows/reduceWorkflows';
import {
  STAGE_RUN_FULL_SHAPE,
  STAGE_RUN_SAMPLE_FULL_SHAPE,
  StageRun,
  StageRunSample,
} from 'workflows/WorkflowConstants';

/**
 * DEPRECATED:
 * Gets stage runs and stage run samples for a stage entry from the Redux store and passes them as props to
 * a wrapped component.
 * This component was written before we had Redux, and child components should now be directly connecting
 * to the store.
 */

function makeMapStateToProps(): (state: BenchlingState, props: {entryId: string}) => ConnectedProps {
  const mapSubStateToProps = memoizeLastCall((entryId: string, workflowsReduxState: WorkflowsState) => {
    const workflowSelector = new WorkflowSelector(workflowsReduxState);
    if (APIUtil.apiIdToType(entryId) !== ModelTypeConstants.STAGE_ENTRY) {
      return {
        initialStageRunsLoading: false, // Not a stage entry, so not loading stage runs
        stageRuns: [],
        stageRunSampleById: {},
      };
    }
    // Use the new stageEntryIsLoading method
    const isLoading = workflowSelector.stageEntryIsLoading(entryId);
    return {
      initialStageRunsLoading: isLoading,
      // getOrderedStageRunsForEntry should return an array, even if empty when not loading
      stageRuns: isLoading ? [] : workflowSelector.getOrderedStageRunsForEntry(entryId),
      stageRunSampleById: workflowSelector.getStageRunSampleById(),
    };
  });
  const mapStateToProps = (state: BenchlingState, props: {entryId: string}): ConnectedProps =>
    mapSubStateToProps(props.entryId, state.models.workflows);
  return mapStateToProps;
}

interface ConnectedProps {
  initialStageRunsLoading: boolean; // New prop for loading state
  stageRuns: Array<StageRun>; // Should always be an array now
  stageRunSampleById: {[stageRunSampleId: string]: StageRunSample};
}

interface GetStageRunsWrapperProps extends ConnectedProps {
  children: (
    stageRuns: Array<StageRun>,
    stageRunSampleById: {[stageRunSampleId: string]: StageRunSample}
  ) => JSX.Element;
  entryId: string;
}

class GetStageRunsWrapper extends React.Component<GetStageRunsWrapperProps> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static propTypes: any = {
    children: PropTypes.func.isRequired,
    entryId: PropTypes.string.isRequired,

    // Provided by connect
    initialStageRunsLoading: PropTypes.bool.isRequired, // Added prop type for loading state
    stageRuns: PropTypes.arrayOf(STAGE_RUN_FULL_SHAPE.isRequired).isRequired, // Expect an array
    stageRunSampleById: PropTypes.objectOf(STAGE_RUN_SAMPLE_FULL_SHAPE).isRequired,
  };

  render(): JSX.Element {
    // Use initialStageRunsLoading prop
    const {initialStageRunsLoading, stageRuns, stageRunSampleById} = this.props;

    if (initialStageRunsLoading) {
      return <div {...loadingIndicatorAttrs()}>Loading stage runs...</div>;
    }

    // stageRuns is now guaranteed to be an array if not initialStageRunsLoading
    // Check if all required stageRunSamples are loaded
    const allSamplesLoaded = stageRuns.every((run) =>
      run.outputSampleIds.every((id) => stageRunSampleById[id])
    );

    if (!allSamplesLoaded) {
      return <div {...loadingIndicatorAttrs()}>Loading stage run samples...</div>;
    }

    return this.props.children(stageRuns, stageRunSampleById);
  }
}

export default connect(makeMapStateToProps)(GetStageRunsWrapper);
